import React, { useContext, useLayoutEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ApiError } from "go-core/api/types";
import { Loading } from "go-core/components/Loading";
import { CustomValidationError } from "go-form/services/types";
import {
	OrderPaymentMethodApi,
	OrderPaymentMethodType,
	PayOrderData,
} from "../../../../../../../../services/Api/Public/types";
import { googlePayEnvironment, paymentEnvironment } from "../../../../../../../../utils/envutil";
import { OrderContext } from "../../../../contexts/OrderContext";
import { API_VERSION, API_VERSION_MINOR, allowedPaymentMethods, isAppleDevice, isSafari } from "../../../../utils";
import OrderPaymentEmailInput from "../OrderPaymentEmailInput";
import OrderPaymentInvoice from "../OrderPaymentInvoice";
import OrderPaymentMethodOption from "./OrderPaymentMethodOption";

interface Props {
	setSelectedPaymentMethod: React.Dispatch<React.SetStateAction<OrderPaymentMethodApi | undefined>>;
	selectedPaymentMethod: OrderPaymentMethodApi | undefined;
	form: UseFormReturn<PayOrderData>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<PayOrderData>[]>>;
	setSendInvoice: React.Dispatch<React.SetStateAction<boolean>>;
	sendInvoice: boolean;
	customFormErrors: ApiError[];
}

const OrderPayments = ({
	selectedPaymentMethod,
	setSelectedPaymentMethod,
	form,
	setErrors,
	sendInvoice,
	setSendInvoice,
	customFormErrors,
}: Props) => {
	const { order } = useContext(OrderContext);
	const [availablePaymentMethods, setAvailablePaymentMethods] = useState([OrderPaymentMethodType.BLIK]);
	const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

	useLayoutEffect(() => {
		init();
	}, []);

	const init = async () => {
		await checkPaymentMethodsAvailability();
		const firstAvailablePaymentMethod = (order?.payment_methods || []).find((paymentMethod) =>
			isVisible(paymentMethod)
		);
		setSelectedPaymentMethod(firstAvailablePaymentMethod);
	};

	const checkIfApplePayIsAvailable = () => {
		if (window.ApplePaySession) {
			try {
				const value = window.ApplePaySession.canMakePayments();

				if (value) {
					setAvailablePaymentMethods((prevState) => [...prevState, OrderPaymentMethodType.APPLE_PAY]);
				}
			} catch (err) {
				return null;
			}
		}
	};

	const checkIfGooglePayIsAvailable = async () => {
		if (googlePayEnvironment() === "TEST" && !order?.google_merchant_id) {
			setAvailablePaymentMethods((prevState) => [...prevState, OrderPaymentMethodType.GOOGLE_PAY]);
			return;
		}

		if (isSafari || isAppleDevice) return;

		try {
			const paymentsClient = new window.google.payments.api.PaymentsClient({
				environment: paymentEnvironment(),
			});
			const request = {
				apiVersion: API_VERSION,
				apiVersionMinor: API_VERSION_MINOR,
				allowedPaymentMethods,
				existingPaymentMethodRequired: true,
			};
			const response = await paymentsClient.isReadyToPay(request);

			if (response.result === true) {
				setAvailablePaymentMethods((prevState) => [...prevState, OrderPaymentMethodType.GOOGLE_PAY]);
			}
		} catch (err) {
			return null;
		}
	};

	const checkPaymentMethodsAvailability = async () => {
		setLoadingPaymentMethods(true);
		await checkIfGooglePayIsAvailable();
		checkIfApplePayIsAvailable();
		setLoadingPaymentMethods(false);
	};

	const isVisible = (paymentMethod: OrderPaymentMethodApi) => {
		return availablePaymentMethods.includes(paymentMethod.type);
	};

	if (!order?.payment_methods || order?.payment_methods?.length === 0) return <></>;

	return (
		<section data-testid="e-order-payments-section">
			{loadingPaymentMethods ? (
				<Loading />
			) : (
				<div className="w-100 d-flex flex-column">
					<div className="order-payments w-100 d-flex flex-column">
						{order.payment_methods.map((paymentMethod) => {
							const visible = isVisible(paymentMethod);

							if (visible) {
								return (
									<OrderPaymentMethodOption
										key={`${paymentMethod.id}-${paymentMethod.type}`}
										onClick={() => setSelectedPaymentMethod(paymentMethod)}
										selected={
											selectedPaymentMethod?.id === paymentMethod.id &&
											selectedPaymentMethod?.type === paymentMethod.type
										}
										type={paymentMethod.type}
									/>
								);
							}

							return undefined;
						})}
					</div>
					<OrderPaymentEmailInput form={form} setErrors={setErrors} customFormErrors={customFormErrors} />
					<OrderPaymentInvoice
						form={form}
						setErrors={setErrors}
						sendInvoice={sendInvoice}
						setSendInvoice={setSendInvoice}
						customFormErrors={customFormErrors}
					/>
				</div>
			)}
		</section>
	);
};

export default OrderPayments;
